/**
 * Money & unit helpers.
 *
 * Conventions:
 *   - USD amounts use Decimal (decimal.js). Never use `number` for money math.
 *   - BTC amounts use Decimal with up to 8 decimal places of meaning.
 *   - Sats are stored as bigint when persisted (1 BTC = 100_000_000 sats).
 *   - Percentages and ratios use Decimal, expressed as fractions (0.78, not 78).
 *
 * decimal.js precision is set to 40 significant digits  -  generous headroom for
 * cumulative carry-trade math over 70+ years (840 months).
 */

import Decimal from 'decimal.js';

Decimal.set({
  precision: 40,
  rounding: Decimal.ROUND_HALF_EVEN,
  toExpPos: 40,
  toExpNeg: -40,
});

export { Decimal };

export type Usd = Decimal;
export type Btc = Decimal;
export type Sats = bigint;

export const SATS_PER_BTC = new Decimal(100_000_000);
export const SATS_PER_BTC_BIGINT = 100_000_000n;

/** Construct a Decimal from a number, string, or Decimal  -  null-safe via overloads. */
export function dec(v: Decimal.Value): Decimal {
  return new Decimal(v);
}

export function usd(v: Decimal.Value): Usd {
  return new Decimal(v);
}

export function btc(v: Decimal.Value): Btc {
  return new Decimal(v);
}

export const ZERO = new Decimal(0);
export const ONE = new Decimal(1);

/** BTC → sats (bigint). Truncates fractional sats. */
export function btcToSats(b: Btc): Sats {
  // Multiply by 1e8, truncate to integer.
  const scaled = b.times(SATS_PER_BTC).toFixed(0, Decimal.ROUND_DOWN);
  return BigInt(scaled);
}

/** Sats → BTC (Decimal, exact). */
export function satsToBtc(s: Sats): Btc {
  return new Decimal(s.toString()).div(SATS_PER_BTC);
}

/** Sum a list of Decimals. */
export function sum(values: Decimal[]): Decimal {
  return values.reduce((acc, v) => acc.plus(v), ZERO);
}

/** Maximum of a list. Returns ZERO if empty. */
export function maxOf(values: Decimal[]): Decimal {
  if (values.length === 0) return ZERO;
  return values.reduce((acc, v) => (v.greaterThan(acc) ? v : acc), values[0]!);
}

/**
 * Compare two Decimals for "approximately equal"  -  used in tests and to compare
 * against workbook values that contain floating-point artifacts.
 */
export function approxEqual(a: Decimal.Value, b: Decimal.Value, tolerance: Decimal.Value = '1e-9'): boolean {
  return new Decimal(a).minus(new Decimal(b)).abs().lessThanOrEqualTo(tolerance);
}

/** Format a Decimal as a USD string with 2 decimal places. */
export function formatUsd(v: Usd): string {
  return v.toFixed(2);
}

/** Format BTC with 8 decimal places (the smallest meaningful unit, 1 sat). */
export function formatBtc(v: Btc): string {
  return v.toFixed(8);
}

/** Format sats as a thousands-separated integer. */
export function formatSats(s: Sats): string {
  return s.toLocaleString('en-US');
}
